import React, { useState, useEffect, useCallback } from "react"
import DataTable from "aldoo-ra/DataTable"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import ObjectBrowser from "./ObjectBrowser"
import {
  listNodes,
  listDbModels,
  listModelItems,
  listClassifications,
  assignClassification,
  removeClassification,
  getClassifiedIds,
  getClassifiedItem,
  updateClassification,
} from "../CategoryManager/api"
import { XMarkIcon, TagIcon } from "@heroicons/react/24/outline"

// CMS meta-types that are part of the CMS system itself, not user content
const CMS_META_TYPES = new Set(["ContentType", "FieldType", "Group", "Editor"])

export default function ClassifyTab() {
  const { availableTypeNames } = TypeResolver()
  const [dbModels, setDbModels] = useState([])
  const [nodes, setNodes] = useState([])
  // { value: string, kind: "cms" | "db" } | null
  const [dataSource, setDataSource] = useState(null)
  const [selectedNode, setSelectedNode] = useState(null)
  const [classifiedIds, setClassifiedIds] = useState([])
  const [itemsRefreshKey, setItemsRefreshKey] = useState(0)
  const [assocRefreshKey, setAssocRefreshKey] = useState(0)
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [selectedItemData, setSelectedItemData] = useState([])
  const [browserItem, setBrowserItem] = useState(null)
  const [browserTitle, setBrowserTitle] = useState("")
  const [typeFilter, setTypeFilter] = useState([])
  const [classificationTypes, setClassificationTypes] = useState([])

  // Load DB model names from server
  useEffect(() => {
    listDbModels().then((res) => {
      if (res?.data) setDbModels(res.data)
    })
  }, [])

  // Load taxonomy nodes
  useEffect(() => {
    listNodes({ includeAll: true }).then((res) => {
      if (!res?.list) return
      setNodes(
        res.list.map((n) => ({
          value: n.content?.slug,
          label: n.content?.name || n.content?.slug,
        }))
      )
    })
  }, [])

  // Refresh classified IDs when data source or node changes
  useEffect(() => {
    setClassifiedIds([])
    if (!dataSource?.value || !selectedNode) return
    getClassifiedIds(dataSource.value, selectedNode).then((res) => {
      if (res?.data) setClassifiedIds(res.data)
    })
  }, [dataSource?.value, selectedNode, assocRefreshKey])

  // Reset type filter when node changes
  useEffect(() => {
    setTypeFilter([])
    setClassificationTypes([])
  }, [selectedNode])

  // Build grouped data source options
  const dataSourceOptions = (() => {
    const cmsNames = availableTypeNames().filter((n) => !CMS_META_TYPES.has(n)).sort()
    const opts = []

    opts.push({ value: "__cms_header__", label: "CMS Types", disabled: true })
    cmsNames.forEach((name) =>
      opts.push({ value: name, label: name, _kind: "cms" })
    )

    if (dbModels.length > 0) {
      opts.push({ divider: true })
      opts.push({ value: "__db_header__", label: "DB Models", disabled: true })
      dbModels.forEach((name) =>
        opts.push({ value: name, label: name, _kind: "db" })
      )
    }

    return opts
  })()

  // Items table API — always provided; returns empty when no data source selected
  const itemsApiMethod = useCallback(
    (params) => {
      if (!dataSource) return Promise.resolve({ data: [], total: 0 })

      if (dataSource.kind === "db") {
        return listModelItems({
          modelName: dataSource.value,
          page: params.page,
          itemsPerPage: params.itemsPerPage,
          search: params.search,
        })
      }

      return GetCMSContent({
        ...params,
        match: {
          "content.type": dataSource.value,
          deleted: { $ne: true },
          ...(params.match || {}),
        },
      })
    },
    [dataSource]
  )

  // Associations table API — always provided; returns empty when no node selected
  const assocApiMethod = useCallback(
    (params) => {
      if (!selectedNode) return Promise.resolve({ data: [], total: 0 })
      const match = { taxonomySlug: selectedNode }
      if (dataSource?.value) match.dataType = dataSource.value
      if (typeFilter.length > 0) match.dataType = { $in: typeFilter.map((t) => t.value) }
      return listClassifications({ ...params, match })
    },
    [selectedNode, dataSource?.value, typeFilter]
  )

  const handleAssign = async () => {
    if (!dataSource?.value || !selectedNode || selectedItemIds.length === 0) return
    const res = await assignClassification(
      selectedItemIds.map(String),
      dataSource.value,
      selectedNode
    )
    if (res?.result) {
      showNotification({
        message: `Assigned ${res.assigned} item(s)`,
        className: "bg-admin_success text-white",
      })
      setSelectedItemIds([])
      setSelectedItemData([])
      setAssocRefreshKey((k) => k + 1)
    } else {
      showNotification({
        message: res?.error || "Assignment failed",
        className: "bg-admin_error text-white",
      })
    }
  }

  const handleRemove = async (record) => {
    const res = await removeClassification({ ids: [record._id] })
    if (res?.result) {
      showNotification({
        message: "Removed classification",
        className: "bg-admin_success text-white",
      })
      setAssocRefreshKey((k) => k + 1)
    }
  }

  const handleToggleEnabled = async (record) => {
    const res = await updateClassification(record._id, !record.enabled)
    if (res?.result) {
      setAssocRefreshKey((k) => k + 1)
    } else {
      showNotification({ message: "Failed to update", className: "bg-admin_error text-white" })
    }
  }

  const handleOpenBrowser = async (record) => {
    const res = await getClassifiedItem(record.dataId, record.dataType)
    if (res?.data) {
      setBrowserItem(res.data)
      setBrowserTitle(record._itemName || record.dataId)
    }
  }

  const nodeOptions = [...nodes]

  // Resolve display name for an item regardless of data source kind
  const getItemName = (item) =>
    item.content?.name ||
    item.content?.title ||
    item.name ||
    item.title ||
    item.email ||
    item.displayName ||
    String(item._id)

  const itemColumns = [
    {
      id: "name",
      title: "Name",
      render: (_, item) => (
        <span className="truncate block w-full">{getItemName(item)}</span>
      ),
    },
    {
      id: "taxonomy",
      title: "Node",
      headerClassName: "w-48",
      render: (_, item) => {
        const isClassified = classifiedIds.includes(String(item._id))
        if (!isClassified) return <span className="text-admin_text_dim text-xs">—</span>
        return (
          <span className="inline-flex items-center gap-1 text-xs bg-admin_primary/10 text-admin_primary rounded-full px-2 py-0.5">
            <TagIcon className="w-3 h-3" />
            {selectedNode || "tagged"}
          </span>
        )
      },
    },
  ]

  const assocColumns = [
    {
      id: "_itemName",
      title: "Item",
      render: (v, record) => (
        <button
          className="text-left truncate block w-full text-admin_primary hover:underline"
          onClick={() => handleOpenBrowser(record)}
          title="View item details"
        >
          {v || record.dataId}
        </button>
      ),
    },
    {
      id: "dataType",
      title: "Type",
      headerClassName: "w-32",
      render: (v) => <span className="text-admin_text_secondary text-xs">{v}</span>,
    },
    {
      id: "enabled",
      title: "Enabled",
      headerClassName: "w-20 text-center",
      render: (v, record) => (
        <div className="flex justify-center">
          <Checkbox
            variant="admin"
            isOn={!!record.enabled}
            onChange={() => handleToggleEnabled(record)}
          />
        </div>
      ),
    },
    {
      id: "actions",
      title: "",
      headerClassName: "w-10",
      render: (_, record) => (
        <Button
          variant="admin_icon"
          className="p-1 text-admin_text_secondary hover:text-admin_error"
          onClick={() => handleRemove(record)}
          title="Remove"
        >
          <XMarkIcon className="w-4 h-4" />
        </Button>
      ),
    },
  ]

  const itemsFooter = selectedItemIds.length > 0 && (
    <div className="flex items-center justify-between px-4 py-2 bg-admin_paper border-t border-admin_bordered">
      <Typography variant="admin_body">
        <span className="font-bold">{selectedItemIds.length}</span> item(s) selected
      </Typography>
      <div className="flex gap-2">
        <Button
          variant="admin_secondary"
          onClick={() => {
            setSelectedItemIds([])
            setSelectedItemData([])
          }}
        >
          Deselect
        </Button>
        <Button
          variant="admin_primary"
          onClick={handleAssign}
          disabled={!selectedNode}
          title={!selectedNode ? "Select a taxonomy node first" : undefined}
        >
          Assign to node
        </Button>
      </div>
    </div>
  )

  const selectedDataSourceOption = dataSource ? [{ value: dataSource.value }] : []

  return (
    <div className="flex flex-col h-full bg-admin_canvas">
      {/* Filters bar */}
      <div className="flex items-end gap-3 px-4 py-3 bg-admin_paper border-b border-admin_bordered">
        <div className="flex flex-col gap-1 w-64">
          <Typography variant="admin_txt2_dim">Data Source</Typography>
          <DropDownSelect
            variant="admin"
            options={dataSourceOptions}
            value={selectedDataSourceOption}
            onChange={(vals) => {
              const opt = vals?.[0]
              if (!opt?.value || opt.value.startsWith("__")) {
                setDataSource(null)
                setSelectedItemIds([])
                setSelectedItemData([])
                setItemsRefreshKey((k) => k + 1)
                return
              }
              setDataSource({ value: opt.value, kind: opt._kind || "cms" })
              setSelectedItemIds([])
              setSelectedItemData([])
              setItemsRefreshKey((k) => k + 1)
            }}
            showCheckbox={false}
            showSelectionTick
            highlightOnSelection
            searchEnabled
            placeholder="Select data source"
          />
        </div>

        <div className="flex flex-col gap-1 w-56">
          <Typography variant="admin_txt2_dim">Taxonomy Node</Typography>
          <DropDownSelect
            variant="admin"
            options={nodeOptions}
            value={selectedNode ? nodeOptions.filter((o) => o.value === selectedNode) : []}
            onChange={(vals) => {
              const v = vals?.[0]?.value ?? null
              setSelectedNode(v)
              setAssocRefreshKey((k) => k + 1)
            }}
            showCheckbox={false}
            showSelectionTick
            highlightOnSelection
            searchEnabled
            placeholder="Select node"
          />
        </div>
      </div>

      {/* Two tables side by side */}
      <div className="flex flex-row gap-4 px-4 pb-4 mt-4">
        {/* Items table */}
        <div className="flex flex-col flex-1 bg-admin_paper rounded border border-admin_bordered overflow-hidden">
          <div className="px-4 pt-3 pb-1">
            <Typography variant="admin_h4">Content Items</Typography>
            <Typography variant="admin_txt2_dim">
              {dataSource
                ? `${dataSource.kind === "db" ? "DB model" : "CMS type"}: ${dataSource.value}`
                : "Select a data source to browse items"}
            </Typography>
          </div>
          <DataTable
            variant="admin_browser"
            apiMethod={itemsApiMethod}
            apiDataField="data"
            renders={itemColumns}
            searchField={
              dataSource?.kind === "db" ? "name email title" : "content.name content.title"
            }
            refreshKey={`${itemsRefreshKey}-${dataSource?.value}`}
            preferredHeight="calc(100vh - 440px)"
            itemsPerPage={15}
            enableSelection
            selectedItems={selectedItemIds}
            selectedData={selectedItemData}
            onSelectionChange={(ids, data) => {
              setSelectedItemIds(ids)
              setSelectedItemData(data)
            }}
            itemIdField="_id"
            tableFooter={itemsFooter}
            noData={
              <Typography variant="admin_body" className="text-admin_text_dim">
                {dataSource ? "No items found" : "Select a data source"}
              </Typography>
            }
          />
        </div>

        {/* Associations table */}
        <div className="flex flex-col flex-1 bg-admin_paper rounded border border-admin_bordered overflow-hidden">
          <div className="px-4 pt-3 pb-2">
            <Typography variant="admin_h4">Classifications</Typography>
            <Typography variant="admin_txt2_dim">
              {selectedNode
                ? dataSource ? `Node "${selectedNode}" — ${dataSource.value}` : `Node "${selectedNode}"`
                : "Select a taxonomy node"}
            </Typography>
            {classificationTypes.length > 0 && (
              <div className="mt-2">
                <DropDownSelect
                  variant="admin"
                  options={classificationTypes}
                  value={typeFilter}
                  onChange={(vals) => {
                    setTypeFilter(vals || [])
                    setAssocRefreshKey((k) => k + 1)
                  }}
                  multiSelection
                  searchEnabled
                  showCheckbox
                  placeholder="Filter by type"
                />
              </div>
            )}
          </div>
          <DataTable
            variant="admin_browser"
            apiMethod={assocApiMethod}
            apiDataField="data"
            renders={assocColumns}
            searchField="_itemName dataType dataId"
            refreshKey={`${assocRefreshKey}-${selectedNode}-${dataSource?.value}-${typeFilter.map((t) => t.value).join(",")}`}
            preferredHeight="calc(100vh - 440px)"
            itemsPerPage={15}
            onDataLoaded={(data) => {
              const types = [...new Set(data.map((r) => r.dataType).filter(Boolean))]
              setClassificationTypes((prev) => {
                const existing = new Set(prev.map((t) => t.value))
                const merged = [...prev]
                types.forEach((t) => {
                  if (!existing.has(t)) merged.push({ value: t, label: t })
                })
                return merged
              })
              return data
            }}
            noData={
              <Typography variant="admin_body" className="text-admin_text_dim">
                {selectedNode ? "No classifications" : "Select a taxonomy node"}
              </Typography>
            }
          />
        </div>
      </div>

      <ObjectBrowser
        item={browserItem}
        title={browserTitle}
        onClose={() => setBrowserItem(null)}
      />
    </div>
  )
}
